import { BUSINESS_DATE_FIELD_VALUE } from 'app/modules/detectionModels/constants';
import {
  IRSchema,
  DmDurationUnits,
  DurationValue,
  ValueTypes,
  DmFactTypes,
} from 'app/modules/rulesAdvanced/models';
import { toSentenceCase } from 'app/shared/utils/string';
import { invert } from 'lodash';

export const MONTH_UNIT_MAP = {
  fdom: 'the first day of the month',
  ldom: 'the last day of the month',
  fmom: 'the first Monday of the month',
  lfom: 'the last Friday of the month',
};

const DM_DURATION_UNITS: readonly DmDurationUnits[] = [
  DmDurationUnits.MINUTE,
  DmDurationUnits.HOUR,
  DmDurationUnits.DAY,
  DmDurationUnits.WEEK,
  DmDurationUnits.MONTH,
  DmDurationUnits.YEAR,
];

export const isDmDurationUnit = (
  value: string | undefined,
): value is DmDurationUnits => {
  if (!value) return false;
  return (DM_DURATION_UNITS as readonly string[]).includes(value);
};

export const DURATION_UNIT_MAP: { [key: string]: DmDurationUnits } = {
  min: DmDurationUnits.MINUTE,
  h: DmDurationUnits.HOUR,
  d: DmDurationUnits.DAY,
  w: DmDurationUnits.WEEK,
  m: DmDurationUnits.MONTH,
  y: DmDurationUnits.YEAR,
};

export const durationOptions: {
  text: string;
  value: DmDurationUnits;
}[] = Object.values(DURATION_UNIT_MAP).map((unit) => {
  return {
    text: toSentenceCase(unit),
    value: unit,
  };
});

export const pluralizedDurationOptions: {
  text: string;
  value: DmDurationUnits;
}[] = Object.values(DURATION_UNIT_MAP).map((unit) => {
  return {
    text: `${toSentenceCase(unit)}(s)`,
    value: unit,
  };
});

export const DURATION_UNIT_MAP_INVERTED = invert(DURATION_UNIT_MAP);

export const TIME_CONVERSION: Record<DmDurationUnits, number> = {
  MINUTE: 1,
  HOUR: 60,
  DAY: 60 * 24,
  WEEK: 60 * 24 * 7,
  MONTH: 60 * 24 * 31,
  YEAR: 60 * 24 * 365,
};

export const DAYS_IN_ONE_YEAR = 365.0;

export function getWindowSizeFromIR(rule: IRSchema): string {
  let windowLookbackInMin = 10;
  let node: DurationValue = {
    value: 10,
    unit: DmDurationUnits.MINUTE,
    type: ValueTypes.DURATION,
  };
  let containsValueFactsOnly = true;
  rule.facts.forEach((fact) => {
    if (
      fact.type === DmFactTypes.EVENT_BY_OBJECT_FACT ||
      fact.type === DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION
    ) {
      containsValueFactsOnly = false;
      const windowSize =
        fact.window_start_offset.value *
        TIME_CONVERSION[fact.window_start_offset.unit];

      if (windowLookbackInMin < windowSize) {
        windowLookbackInMin = windowSize;
        node = fact.window_start_offset;
      }
    }
  });

  if (containsValueFactsOnly) {
    node = {
      value: 1,
      unit: DmDurationUnits.MONTH,
      type: ValueTypes.DURATION,
    };
  }

  return (
    node.value +
    (node.unit !== undefined
      ? DURATION_UNIT_MAP_INVERTED[node.unit]
      : DmDurationUnits.MINUTE)
  );
}

export const getForcedExecutionIntervalFromIR = (rule: IRSchema): string[] => {
  const additionalIntervalOptions: string[] = [];

  const businessDateFact = rule.facts.find((fact) => {
    if (fact.type === DmFactTypes.EVENT_BY_OBJECT_FACT) {
      return fact.window_field?.field === BUSINESS_DATE_FIELD_VALUE.field;
    }
    return false;
  });

  if (businessDateFact) {
    additionalIntervalOptions.push('1d');
  }
  return additionalIntervalOptions;
};
